import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, CardBody, Col, Row } from 'reactstrap';
import AutorenewIcon from 'mdi-react/AutorenewIcon';
import LoadingIcon from 'mdi-react/LoadingIcon';
import ClockOutlineIcon from 'mdi-react/ClockOutlineIcon';
import {
  aggregateSnapshotRows,
  BRAZIL_STATES,
  getSeverityFill,
  getStateByCode,
  getStateByUf
} from './BrazilStatusMap.utils';
import { ReactComponent as BrazilMapSvg } from './br.svg';

const formatNumber = (value) =>
  new Intl.NumberFormat('pt-BR').format(Number(value || 0));

const formatPercent = (value) =>
  `${Math.round(Number(value || 0) * 100)}%`;

const formatInvalidationCount = (value) => {
  const total = Number(value || 0);
  return `${formatNumber(total)} ${total === 1 ? 'invalidação' : 'invalidações'}`;
};

const getSeverityMeta = (severity, hasData) => {
  if (!hasData) return { label: 'Sem dados', className: 'muted' };
  if (severity >= 0.6) return { label: 'Alto', className: 'critical' };
  if (severity >= 0.4) return { label: 'Atenção', className: 'warning' };
  return { label: 'Saudável', className: 'healthy' };
};

const formatGroupName = (group) => {
  switch (group) {
    case 'VALIDADO':
      return 'Validado';
    case 'INVALIDO':
      return 'Inválido';
    case 'NA FILA':
      return 'Na fila';
    case 'FREEZER':
      return 'Aguardando';
    case 'COLETANDO':
      return 'Coletando';
    case 'SEM_STATUS':
      return 'Sem status';
    default:
      return group;
  }
};

const emptySummary = {
  total: 0,
  statuses: [],
  errors: [],
  severity: 0
};

const BrazilStatusMap = ({
  snapshotState,
  selectedStateCode,
  onSelectState
}) => {
  const { rows, refresh, error, lastUpdatedAt, reload: getSnapshot } = snapshotState;
  const [previewUf, setPreviewUf] = useState(null);
  const mapRef = useRef(null);

  const selectedState = getStateByCode(selectedStateCode);
  const selectedUf = selectedState?.uf || null;
  const statesBySvgId = useMemo(
    () => BRAZIL_STATES.reduce((acc, state) => {
      acc[`BR${state.uf}`] = state;
      return acc;
    }, {}),
    []
  );

  const snapshot = useMemo(() => aggregateSnapshotRows(rows), [rows]);
  const activeUf = selectedUf || previewUf;
  const activeState = activeUf ? getStateByUf(activeUf) : null;
  const activeSummary = activeUf
    ? snapshot.byUf[activeUf] || { ...emptySummary, uf: activeUf }
    : snapshot.national || emptySummary;
  const invalidationsTotal = activeSummary.errors.reduce(
    (sum, item) => sum + Number(item.value || 0),
    0
  );
  const invalidationPercent = formatPercent(activeSummary.severity);
  const severityMeta = getSeverityMeta(
    activeSummary.severity,
    activeSummary.total > 0
  );
  const severityClassName = [
    'dashboard__brazil-map-severity',
    `dashboard__brazil-map-severity--${severityMeta.className}`
  ].join(' ');
  const progressClassName = [
    'dashboard__brazil-map-progress-fill',
    `dashboard__brazil-map-progress-fill--${severityMeta.className}`
  ].join(' ');
  const invalidationClassName = [
    'dashboard__brazil-map-invalidation',
    `dashboard__brazil-map-invalidation--${severityMeta.className}`
  ].join(' ');

  useEffect(() => {
    if (!mapRef.current) return;

    BRAZIL_STATES.forEach((state) => {
      const path = mapRef.current.querySelector(`#features path#BR${state.uf}`);
      const summary = snapshot.byUf[state.uf];
      if (!path) return;

      path.setAttribute('role', 'button');
      path.setAttribute('tabindex', '0');
      path.setAttribute(
        'aria-label',
        `${state.name}: ${formatNumber(summary?.total)} notas`
      );
      path.style.fill = getSeverityFill(summary?.severity || 0, !!summary?.total);
      path.classList.toggle(
        'dashboard__brazil-map-state--selected',
        selectedUf === state.uf
      );
      path.classList.toggle(
        'dashboard__brazil-map-state--preview',
        previewUf === state.uf
      );
    });
  }, [previewUf, selectedUf, snapshot]);

  const getStateFromEvent = (event) => {
    const target = event.target.closest
      ? event.target.closest('#features path[id^="BR"]')
      : null;

    return target ? statesBySvgId[target.id] : null;
  };

  const handleMapMouseOver = (event) => {
    const state = getStateFromEvent(event);
    if (state) setPreviewUf(state.uf);
  };

  const selectState = (state) => {
    onSelectState(selectedUf === state.uf ? 'ALL' : state.code);
  };

  const handleMapClick = (event) => {
    const state = getStateFromEvent(event);
    if (state) selectState(state);
  };

  const handleStateKeyDown = (event) => {
    const state = getStateFromEvent(event);
    if (!state) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      selectState(state);
    }
  };

  return (
    <Card>
      <CardBody className="panel__body dashboard__brazil-map-card">
        {refresh ? (
          <div className="panel__refresh">
            <LoadingIcon />
          </div>
        ) : (
          ''
        )}
        <div className="panel__btns">
          <button
            className="panel__btn"
            aria-label="Atualizar mapa de status"
            type="button"
            onClick={getSnapshot}
          >
            <AutorenewIcon />
          </button>
        </div>
        <div className="panel__title">
          <h5 className="bold-text">Status das notas por estado</h5>
          <p className="dashboard__brazil-map-updated" aria-live="polite">
            <ClockOutlineIcon size={14} aria-hidden="true" />
            <span>Última atualização:</span>{' '}
            {lastUpdatedAt ? (
              <time dateTime={lastUpdatedAt}>
                {new Date(lastUpdatedAt).toLocaleString('pt-BR')}
              </time>
            ) : 'Ainda não atualizado'}
          </p>
        </div>

        <Row className="dashboard__brazil-map-content">
          <Col md={7}>
            <div
              ref={mapRef}
              className="dashboard__brazil-map-wrap"
              role="presentation"
              onClick={handleMapClick}
              onMouseOver={handleMapMouseOver}
              onMouseLeave={() => setPreviewUf(null)}
              onFocus={(event) => {
                const state = getStateFromEvent(event);
                if (state) setPreviewUf(state.uf);
              }}
              onBlur={() => setPreviewUf(null)}
              onKeyDown={handleStateKeyDown}
            >
              <BrazilMapSvg
                className="dashboard__brazil-map"
                aria-label="Mapa do Brasil por status de notas"
                role="img"
              />
            </div>
          </Col>
          <Col md={5}>
            <div className="dashboard__brazil-map-details">
              <div className="dashboard__brazil-map-heading">
                <div>
                  <span className="dashboard__brazil-map-eyebrow">
                    {activeState ? activeState.uf : 'BR'}
                  </span>
                  <h4>{activeState ? activeState.name : 'Todos os estados'}</h4>
                </div>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => onSelectState('ALL')}
                  disabled={!selectedUf}
                >
                  Todos
                </button>
              </div>

              {error ? (
                <p className="text-content m-0">
                  Não foi possível carregar o status dos estados.
                </p>
              ) : (
                <>
                  <div className="dashboard__brazil-map-summary">
                    <div className="dashboard__brazil-map-total">
                      <span>Total de notas</span>
                      <strong>{formatNumber(activeSummary.total)}</strong>
                    </div>

                    <div className={invalidationClassName}>
                      <div className="dashboard__brazil-map-invalidation-heading">
                        <span>Invalidações</span>
                        <span className={severityClassName}>
                          {severityMeta.label}
                        </span>
                      </div>
                      <strong>{invalidationPercent}</strong>
                      <div
                        className="dashboard__brazil-map-progress"
                        aria-label={`Percentual de invalidações: ${invalidationPercent}`}
                      >
                        <span className={progressClassName} style={{ width: invalidationPercent }} />
                      </div>
                      <p>{formatInvalidationCount(invalidationsTotal)}</p>
                    </div>
                  </div>

                  {activeSummary.total > 0 ? (
                    <>
                      <div className="dashboard__brazil-map-section">
                        <div className="dashboard__brazil-map-section-title">
                          <h6>Status</h6>
                        </div>
                        <ul>
                          {activeSummary.statuses.map((status) => (
                            <li key={status.name}>
                              <span>{formatGroupName(status.name)}</span>
                              <strong>{formatNumber(status.value)}</strong>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="dashboard__brazil-map-section">
                        <div className="dashboard__brazil-map-section-title">
                          <h6>Motivos de Invalidação</h6>
                        </div>
                        {activeSummary.errors.length > 0 ? (
                          <ul>
                            {activeSummary.errors.map((item) => (
                              <li key={item.message}>
                                <span title={item.message}>{item.message}</span>
                                <strong>{formatNumber(item.value)}</strong>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-content m-0">
                            Nenhuma invalidação encontrada nesse período.
                          </p>
                        )}
                      </div>
                    </>
                  ) : (
                    <p className="text-content m-0">
                      Não foi encontrado nenhum dado nesse periodo.
                    </p>
                  )}
                </>
              )}
            </div>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

BrazilStatusMap.propTypes = {
  snapshotState: PropTypes.shape({
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
    refresh: PropTypes.bool.isRequired,
    error: PropTypes.bool.isRequired,
    reload: PropTypes.func.isRequired,
    lastUpdatedAt: PropTypes.string
  }).isRequired,
  selectedStateCode: PropTypes.string,
  onSelectState: PropTypes.func.isRequired
};

BrazilStatusMap.defaultProps = {
  selectedStateCode: 'ALL'
};

export default BrazilStatusMap;
